import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { INodeConfig } from '@/types/docflow';

interface SheetOutputConfigProps {
  config: INodeConfig;
  onChange: (config: INodeConfig) => void;
}

const SheetOutputConfig: React.FC<SheetOutputConfigProps> = ({ config, onChange }) => {
  const { t } = useTranslation('docflow');

  // Extract spreadsheet ID from URL or use raw ID
  const handleSpreadsheetInput = useCallback((input: string) => {
    let spreadsheetId = input;

    // Extract ID from Google Sheets URL
    const match = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      spreadsheetId = match[1];
    }

    onChange({ ...config, spreadsheet_id: spreadsheetId });
  }, [config, onChange]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {t('node.sheetOutput')}
      </h3>

      {/* Spreadsheet URL/ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.spreadsheetUrl')}
        </label>
        <input
          type="text"
          value={config.spreadsheet_id || ''}
          onChange={(e) => handleSpreadsheetInput(e.target.value)}
          placeholder="Paste Google Sheets URL or ID"
          className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
        />
      </div>

      {/* Sheet tab name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('config.sheetName')}
        </label>
        <input
          type="text"
          value={config.sheet_name || ''}
          onChange={(e) => onChange({ ...config, sheet_name: e.target.value })}
          placeholder="DocFlow Output"
          className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
        />
      </div>

      {/* Write header toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="headerRow"
          checked={config.header_row || false}
          onChange={(e) => onChange({ ...config, header_row: e.target.checked })}
          className="w-4 h-4"
        />
        <label htmlFor="headerRow" className="text-sm text-gray-700 dark:text-gray-300">
          {t('config.headerRow')}
        </label>
      </div>
    </div>
  );
};

export default SheetOutputConfig;
